// src/pages/Editor.jsx
import React, { useState, useCallback } from 'react';
import { Plus } from 'lucide-react'; // Ajout de l'import manquant
import useTemplates from '../hooks/useTemplate';
import useVariables from '../hooks/useVariables';
import EditorToolbar from '../components/editor/EditorToolbar';
import LayerPanel from '../components/editor/LayerPanel';
import EnhancedCanvas from '../components/editor/EnhancedCanvas';
import PropertyPanel from '../components/editor/PropertyPanel';
import DynamicContentManager from '../components/editor/DynamicContentManager';
import VariableManager from '../components/editor/VariableManager';

const Editor = () => {
  const {
    templates,
    currentTemplate,
    selectedElement,
    updateTemplate,
    updateElement,
    selectElement,
    createTemplate,
    setCurrentTemplate,
    isLoading
  } = useTemplates();

  const {
    variables,
    setVariables,
    addVariable,
    updateVariable,
    deleteVariable
  } = useVariables();

  // États locaux
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentView, setCurrentView] = useState('editor'); // 'editor' | 'dynamic' | 'variables'
  const [panelStates, setPanelStates] = useState({
    templates: true,
    layers: true,
    properties: true
  });
  const [canvasFormat, setCanvasFormat] = useState('fullhd-landscape');

  // Formats de canvas
  const canvasFormats = {
    'fullhd-landscape': { width: 1920, height: 1080, label: 'Full HD Paysage' },
    'fullhd-portrait': { width: 1080, height: 1920, label: 'Full HD Portrait' },
    '4k-landscape': { width: 3840, height: 2160, label: '4K Paysage' },
    'custom': { width: 1920, height: 1080, label: 'Personnalisé' }
  };

  const currentCanvasSize = canvasFormats[canvasFormat];
  const dynamicElementsCount = currentTemplate?.elements?.filter(el => el.isDynamic && el.dynamicTag)?.length || 0;

  // ... Reste du code du composant (handlers, etc.)

  // Composant Template Panel avec import corrigé
  const TemplatePanel = ({ templates, currentTemplate, onSelectTemplate, onNewTemplate }) => (
    <div className="full-height flex flex-col bg-white border-r">
      <div className="p-4 border-b flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Templates</h3>
          <button
            onClick={onNewTemplate}
            className="p-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
          >
            <Plus className="h-4 w-4" />
         </button>
       </div>
     </div>
     
     <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
       <div className="space-y-2">
         {templates.map(template => (
           <div
             key={template.id}
             onClick={() => onSelectTemplate(template)}
             className={`p-3 rounded-lg cursor-pointer transition-colors ${
               currentTemplate?.id === template.id
                 ? 'bg-blue-100 border border-blue-300'
                 : 'bg-gray-50 hover:bg-gray-100'
             }`}
           >
             <div className="font-medium text-sm">{template.name}</div>
             <div className="text-xs text-gray-500">{template.category}</div>
             <div className="text-xs text-gray-400 mt-1">
               {template.elements?.length || 0} éléments
               {template.elements?.filter(el => el.isDynamic)?.length > 0 && (
                 <span className="ml-2 text-orange-600">
                   • {template.elements.filter(el => el.isDynamic).length} dynamiques
                 </span>
               )}
             </div>
           </div>
         ))}
       </div>
     </div>
   </div>
 );

 // Gestionnaires d'événements
 const handleAddElement = useCallback((type) => {
   if (!currentTemplate) return;

   let newElement;
   const baseElement = {
     id: `${type}-${Date.now()}`,
     type,
     x: 100,
     y: 100,
     zIndex: (currentTemplate.elements?.length || 0) + 1,
     visible: true
   };

   switch (type) {
     case 'text':
       newElement = {
         ...baseElement,
         content: 'Nouveau texte',
         fontSize: 24,
         color: '#ffffff',
         fontFamily: 'sans-serif',
         fontWeight: 'normal',
         fontStyle: 'normal',
         backgroundColor: 'transparent',
         isDynamic: false,
         enablePriceFormat: false,
         priceFormat: {
           beforeEuroSize: 100,
           beforeEuroHeight: 0,
           euroSize: 40,
           euroHeight: -20,
           afterEuroSize: 40,
           afterEuroHeight: -20
         }
       };
       break;
     case 'image':
       newElement = {
         ...baseElement,
         src: '/media/images/demo.png',
         alt: 'Image',
         width: 200,
         height: 150,
         objectFit: 'cover'
       };
       break;
     case 'video':
       newElement = {
         ...baseElement,
         src: '/media/videos/MENU Dej.mp4',
         width: 320,
         height: 240,
         autoplay: true,
         loop: true,
         muted: true
       };
       break;
   }

   const updatedTemplate = {
     ...currentTemplate,
     elements: [...(currentTemplate.elements || []), newElement]
   };
   
   updateTemplate(updatedTemplate);
   selectElement(newElement);
   setHasUnsavedChanges(true);
 }, [currentTemplate, updateTemplate, selectElement]);

 const handleMoveLayer = useCallback((elementId, direction) => {
   if (!currentTemplate || !currentTemplate.elements) return;
   
   const element = currentTemplate.elements.find(el => el.id === elementId);
   if (!element) return;

   const newZIndex = direction === 'up' 
     ? (element.zIndex || 1) + 1 
     : Math.max(1, (element.zIndex || 1) - 1);

   const updatedTemplate = {
     ...currentTemplate,
     elements: currentTemplate.elements.map(el => 
       el.id === elementId ? { ...el, zIndex: newZIndex } : el
     )
   };

   updateTemplate(updatedTemplate);
   setHasUnsavedChanges(true);
 }, [currentTemplate, updateTemplate]);

 const handleDeleteElement = useCallback((elementId) => {
   if (!currentTemplate) return;
   
   const updatedTemplate = {
     ...currentTemplate,
     elements: (currentTemplate.elements || []).filter(el => el.id !== elementId)
   };
   
   updateTemplate(updatedTemplate);
   
   if (selectedElement?.id === elementId) {
     selectElement(null);
   }
   setHasUnsavedChanges(true);
 }, [currentTemplate, updateTemplate, selectedElement, selectElement]);

 const handleDuplicateElement = useCallback((element) => {
   if (!currentTemplate) return;

   const duplicatedElement = {
     ...element,
     id: `${element.type}-${Date.now()}`,
     x: element.x + 20,
     y: element.y + 20,
     zIndex: (element.zIndex || 0) + 1
   };

   const updatedTemplate = {
     ...currentTemplate,
     elements: [...(currentTemplate.elements || []), duplicatedElement]
   };
   
   updateTemplate(updatedTemplate);
   selectElement(duplicatedElement);
   setHasUnsavedChanges(true);
 }, [currentTemplate, updateTemplate, selectElement]);

 const handleTogglePanel = useCallback((panel) => {
   setPanelStates(prev => ({
     ...prev,
     [panel]: !prev[panel]
   }));
 }, []);

 const handleSave = useCallback(() => {
   setHasUnsavedChanges(false);
   // La sauvegarde est automatique via le hook useTemplates
 }, []);

 const handleTemplateUpdate = useCallback((updates) => {
   if (typeof updates === 'function') {
     const newTemplate = updates(currentTemplate);
     updateTemplate(newTemplate);
   } else {
     updateTemplate({ ...currentTemplate, ...updates });
   }
   setHasUnsavedChanges(true);
 }, [currentTemplate, updateTemplate]);

 const handleElementUpdate = useCallback((elementId, updates) => {
   const updatedTemplate = {
     ...currentTemplate,
     elements: currentTemplate.elements.map(el => 
       el.id === elementId ? { ...el, ...updates } : el
     )
   };
   updateTemplate(updatedTemplate);
   setHasUnsavedChanges(true);
 }, [currentTemplate, updateTemplate]);

 // Largeurs des panneaux
 const PANEL_WIDTHS = {
   templates: 280,
   layers: 320, 
   properties: 320
 };

 if (isLoading) {
   return (
     <div className="full-height-viewport flex items-center justify-center">
       <div className="text-center">
         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
         <p className="text-gray-600">Chargement de l'éditeur...</p>
       </div>
     </div>
   );
 }

 return (
   <div className="editor-layout">
     {/* Barre d'outils */}
     <EditorToolbar
       currentTemplate={currentTemplate}
       hasUnsavedChanges={hasUnsavedChanges}
       dynamicElementsCount={dynamicElementsCount}
       canvasFormat={canvasFormat}
       onCanvasFormatChange={setCanvasFormat}
       onAddElement={handleAddElement}
       onSave={handleSave}
       onPreview={() => console.log('Prévisualisation')}
       onShowDynamicManager={() => setCurrentView('dynamic')}
       panelStates={panelStates}
       onTogglePanel={handleTogglePanel}
       canvasFormats={canvasFormats}
       currentCanvasSize={currentCanvasSize}
       currentView={currentView}
       onViewChange={setCurrentView}
     />

     {/* Zone principale */}
     <div className="editor-main">
       {/* Panneau templates */}
       {panelStates.templates && currentView === 'editor' && (
         <div className="editor-panel-left" style={{ width: `${PANEL_WIDTHS.templates}px` }}>
           <TemplatePanel 
             templates={templates}
             currentTemplate={currentTemplate}
             onSelectTemplate={setCurrentTemplate}
             onNewTemplate={() => createTemplate({ name: `Template ${templates.length + 1}` })}
           />
         </div>
       )}
       
       {/* Panneau couches */}
       {panelStates.layers && currentView === 'editor' && (
         <div className="editor-panel-left" style={{ width: `${PANEL_WIDTHS.layers}px` }}>
           <LayerPanel 
             elements={currentTemplate?.elements || []}
             selectedElement={selectedElement}
             onSelectElement={selectElement}
             onUpdateElement={handleElementUpdate}
             onDeleteElement={handleDeleteElement}
             onDuplicateElement={handleDuplicateElement}
             onMoveLayer={handleMoveLayer}
           />
         </div>
       )}

       {/* Zone centrale */}
       <div className="editor-center">
         {currentView === 'dynamic' ? (
           <div className="full-height flex flex-col">
             <div className="p-4 bg-white border-b flex-shrink-0">
               <div className="flex items-center justify-between">
                 <h2 className="text-lg font-semibold">Gestionnaire de Contenu Dynamique</h2>
                 <div className="flex space-x-2">
                   <button
                     onClick={() => setCurrentView('variables')}
                     className="px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                   >
                     Gérer Variables
                   </button>
                   <button
                     onClick={() => setCurrentView('editor')}
                     className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                   >
                     Retour Éditeur
                   </button>
                 </div>
               </div>
             </div>
             <div className="flex-1 overflow-hidden">
               <DynamicContentManager
                 template={currentTemplate}
                 onUpdateTemplate={handleTemplateUpdate}
                 variables={variables}
               />
             </div>
           </div>
         ) : currentView === 'variables' ? (
           <div className="full-height flex flex-col">
             <div className="p-4 bg-white border-b flex-shrink-0">
               <div className="flex items-center justify-between">
                 <h2 className="text-lg font-semibold">Gestionnaire de Variables</h2>
                 <div className="flex space-x-2">
                   <button
                     onClick={() => setCurrentView('dynamic')}
                     className="px-4 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                   >
                     Gérer Règles
                   </button>
                   <button
                     onClick={() => setCurrentView('editor')}
                     className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                   >
                     Retour Éditeur
                   </button>
                 </div>
               </div>
             </div>
             <div className="flex-1 overflow-hidden p-6">
               <VariableManager
                 variables={variables}
                 onUpdateVariables={setVariables}
               />
             </div>
           </div>
         ) : (
           <EnhancedCanvas
             template={currentTemplate}
             onUpdateTemplate={handleTemplateUpdate}
             selectedElement={selectedElement}
             onSelectElement={selectElement}
             canvasSize={currentCanvasSize}
           />
         )}
       </div>

       {/* Panneau propriétés */}
       {panelStates.properties && currentView === 'editor' && (
          <div className="editor-panel-right" style={{ width: `${PANEL_WIDTHS.properties}px` }}>
            <PropertyPanel 
              selectedElement={selectedElement}
              onUpdateElement={handleElementUpdate}
              variables={variables}
              currentTemplate={currentTemplate}
              onUpdateTemplate={handleTemplateUpdate}
              onShowDynamicManager={() => setCurrentView('dynamic')} // Ajout de cette prop
            />
          </div>
        )}
     </div>
   </div>
 );
};

export default Editor;